<template>
  <div v-if="isLoading">
    <v-skeleton-loader
      type="heading"
      class="topbar-page-title-loader"
      width="200"
    />
    <v-skeleton-loader
      type="text"
      class="topbar-page-title-loader"
      width="400px"
    />
  </div>
  <header v-else class="topbar-page-title" aria-labelledby="page-title-heading">
    <div class="title-row">
      <h1 id="page-title-heading">
        {{ pageTitle }}
      </h1>
      <slot v-if="$slots['title-trailing']" name="title-trailing" />
    </div>

    <p
      v-if="pageDescription || $slots['page-description']"
      class="page-info"
      @click.capture="handleLearnMoreCapture"
      @click.stop
    >
      <slot name="page-description">
        <DpplSafeHtml :html="pageDescription" />
      </slot>
    </p>
  </header>
</template>

<script setup>
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { useRoute } from 'vue-router';

defineProps({
  pageTitle: { type: String, default: '' },
  pageDescription: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
});

const route = useRoute();

const handleLearnMoreCapture = (event) => {
  const learnMoreAnchor = typeof event.target?.closest === 'function'
    ? event.target.closest('.learn-more-preamble-link')
    : null;

  if (!learnMoreAnchor) return;

  if (route.name === 'employee-acknowledgements') {
    useTracking().ackLibClickedLearnMore();
  } else if (route.name === 'job-architecture') {
    useTracking().jaListClickedLearnMore();
  } else if (route.name === 'job-architecture-compare') {
    useTracking().jaMatrixClickedLearnMore();
  } else if (route.name === 'federal-flsa-dashboard') {
    useTracking().flsaLibClickedLearnMore();
  } else if (route.name === 'approvals-library') {
    useTracking().approvalLibClickedLearnMore();
  } else if (route.name === 'flsa-settings') {
    useTracking().flsaLibClickedLearnMore({ page: 'FLSA Settings' });
  } else if (route.name === 'hris-job-profiles') {
    useTracking().jpLibClickedLearnMore({ page: 'Workday Job Profiles Library' });
  } else if (route.name === 'draft-job-profiles') {
    useTracking().jpLibClickedLearnMore({ page: 'Draft Job Profiles Library' });
  } else if (route.name === 'job-profile-templates') {
    useTracking().jpLibClickedLearnMore({ page: 'Job Profile Templates Library' });
  }
};
</script>

<style scoped lang="less">
.topbar-page-title-loader {
    margin-top: 12px;
    background: transparent;
}

:deep(.v-skeleton-loader__heading) {
  margin: 0 16px 0 0;
}

:deep(.v-skeleton-loader__text) {
  margin: 0;
}

.topbar-page-title {
  padding-top: 8px;

  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  h1 {
    font-size: 20px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 150%;
    padding-bottom: 2px;
  }

  .page-info {
    line-height: 150%;

    :deep(p) {
      font-size: 13px;
      font-weight: 500;
      color: @text-light;
      overflow-x: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    :deep(a), :deep(a:visited) {
      font-size: 13px;
      font-weight: 500;
      color: @text-brand;
      text-decoration: underline;
    }
  }
}
</style>
